// src/utils/nutritionRenderer.js
// Renders the nutrition declaration table on the canvas.
//
// The element has a fixed EU-defined row order; the user toggles which
// rows are visible and how the label column is laid out, but they can't
// rearrange the rows. That's intentional — EU regulation 1169/2011
// requires a specific sequence (energy, fat, of-which-saturated, carbs,
// of-which-sugars, protein, salt) and we enforce it here.
//
// DECIMALS (2026-06-14):
// The element has a `decimals` property (0-3, default 1) controlling
// precision on numeric values. It applies to:
//   - All gram values (fat, sat, carbs, sugars, protein, salt)
//   - The kJ and kcal numbers in the energy line
// Strings that parse as numbers are normalised too — so legacy DB rows
// storing "17.000" come out as "17,0g" with decimals=1.
//
// Decimal separator is locale-aware: comma for da/sv/no/fi/de,
// dot for en/ja. Matches lib/dateFormatter.js conventions.

import { mmToPx } from './unitConverter.js'

const DEFAULT_FONT_SIZE_MM = 2.4
const LINE_HEIGHT_FACTOR   = 1.3
const DEFAULT_WIDTH_MM     = 40
const DEFAULT_DECIMALS     = 1

// Locales that use comma as decimal separator. Matches the FORMATS
// table in dateFormatter.js. Everything else falls through to dot.
const COMMA_LOCALES = new Set(['da', 'sv', 'no', 'fi', 'de'])

function decimalSeparator(locale) {
  return COMMA_LOCALES.has(locale) ? ',' : '.'
}

// Parse a value into a finite number, returning null if not possible.
// Handles both numbers and strings; strings get their separator
// normalised before parsing (so "17,5" and "17.5" both work).
function parseNumber(v) {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    // Strip whitespace, normalise the decimal separator to a dot so
    // parseFloat handles it regardless of locale-style input.
    const cleaned = v.trim().replace(',', '.')
    const num = Number(cleaned)
    return Number.isFinite(num) ? num : null
  }
  return null
}

// Format a number with the requested precision in the requested
// locale's decimal separator. Used for gram values; energy formats
// itself.
function formatNumber(value, decimals, locale) {
  const num = parseNumber(value)
  if (num === null) return ''
  const d = Math.min(3, Math.max(0, Number(decimals) ?? DEFAULT_DECIMALS))
  const sep = decimalSeparator(locale)
  return num.toFixed(d).replace('.', sep)
}

const ROWS = [
  { key: 'energy',    visibleFlag: 'showEnergy',    valueToken: 'nutrition_energy', i18nKey: 'energy',    isSub: false },
  { key: 'fat',       visibleFlag: 'showFat',       valueToken: 'nutrition_fat_g',  i18nKey: 'fat',       isSub: false },
  { key: 'saturated', visibleFlag: 'showSaturated', valueToken: 'nutrition_sat_g',  i18nKey: 'saturated', isSub: true  },
  { key: 'carbs',     visibleFlag: 'showCarbs',     valueToken: 'nutrition_carb_g', i18nKey: 'carbs',     isSub: false },
  { key: 'sugars',    visibleFlag: 'showSugars',    valueToken: 'nutrition_sug_g',  i18nKey: 'sugars',    isSub: true  },
  { key: 'protein',   visibleFlag: 'showProtein',   valueToken: 'nutrition_pro_g',  i18nKey: 'protein',   isSub: false },
  { key: 'salt',      visibleFlag: 'showSalt',      valueToken: 'nutrition_salt_g', i18nKey: 'salt',      isSub: false },
]

// Placeholder values used in preview mode when a product without a
// nutrition profile is loaded. Stored as numbers now (not pre-formatted
// strings) so the decimals setting applies to them too — earlier the
// strings were hard-coded and ignored decimals, which made the preview
// inconsistent with what would actually print.
const PLACEHOLDER_VALUES = {
  nutrition_energy_kj:   1638,
  nutrition_energy_kcal:  396,
  nutrition_fat_g:        36.0,
  nutrition_sat_g:        23.0,
  nutrition_carb_g:        2.0,
  nutrition_sug_g:         0.3,
  nutrition_pro_g:        16.0,
  nutrition_salt_g:        1.6,
}

const TOKEN_VALUES = {
  nutrition_energy:  '{energy}',
  nutrition_fat_g:   '{fat}',
  nutrition_sat_g:   '{saturated}',
  nutrition_carb_g:  '{carbs}',
  nutrition_sug_g:   '{sugars}',
  nutrition_pro_g:   '{protein}',
  nutrition_salt_g:  '{salt}',
}

function n(v, fallback = 0) {
  const x = Number(v)
  return Number.isFinite(x) ? x : fallback
}

function isRowVisible(el, row) {
  const flag = el[row.visibleFlag]
  return flag === undefined || flag === null || flag === 1 || flag === true
}

function visibleRows(el) {
  return ROWS.filter(r => isRowVisible(el, r))
}

const FALLBACK_STRINGS = {
  'nutrition.title':        'Næringsindhold',
  'nutrition.per_100g':     'per 100g',
  'nutrition.per_100ml':    'per 100ml',
  'nutrition.per_portion':  'per portion',
  'nutrition.energy':       'Energi',
  'nutrition.fat':          'Fedt',
  'nutrition.saturated':    'heraf mættede fedtsyrer',
  'nutrition.carbs':        'Kulhydrat',
  'nutrition.sugars':       'heraf sukkerarter',
  'nutrition.protein':      'Protein',
  'nutrition.salt':         'Salt',
}

function tr(t, key) {
  if (typeof t === 'function') {
    try {
      const out = t(key)
      if (out && out !== key) return out
    } catch { /* swallow and fall back */ }
  }
  return FALLBACK_STRINGS[key] || key
}

function referenceKey(reference) {
  if (reference === 'per_100ml')   return 'nutrition.per_100ml'
  if (reference === 'per_portion') return 'nutrition.per_portion'
  return 'nutrition.per_100g'
}

export function getNutritionBounds(el) {
  const fontSize  = n(el.fontSize, DEFAULT_FONT_SIZE_MM)
  const lineH     = fontSize * LINE_HEIGHT_FACTOR
  const widthMm   = n(el.elementWidth, DEFAULT_WIDTH_MM)

  let lines = visibleRows(el).length
  if (el.showHeader !== 0 && el.showHeader !== false) lines += 1
  if (el.showReference !== 0 && el.showReference !== false) lines += 1
  if (lines === 0) lines = 1

  return {
    widthMm,
    heightMm: lines * lineH,
  }
}

// Gram values: number + "g". The decimals/locale come from the element
// and the active locale; strings are normalised first via parseNumber.
function formatGrams(v, decimals, locale) {
  const formatted = formatNumber(v, decimals, locale)
  return formatted ? formatted + 'g' : ''
}

// Energy: dual format "1650kJ/1330kcal" with both values rendered to
// the same precision as gram values. The two parts can arrive in
// several shapes:
//   - data.nutrition_energy as a preformatted string (legacy)
//   - data.nutrition_energy_kj + data.nutrition_energy_kcal as numbers
// If only one number is available, the missing half renders as "?".
function formatEnergy(data, decimals, locale) {
  // Preformatted single string — pass through unchanged. The user
  // chose this shape upstream so we don't reformat. (Old fallback
  // path; new profiles store the two numbers separately.)
  if (typeof data.nutrition_energy === 'string' && data.nutrition_energy) {
    return data.nutrition_energy
  }
  const kj   = data.nutrition_energy_kj   ?? data.nutrition_energy
  const kcal = data.nutrition_energy_kcal
  if (kj == null && kcal == null) return ''
  const kjStr   = kj   == null ? '?' : formatNumber(kj,   decimals, locale)
  const kcalStr = kcal == null ? '?' : formatNumber(kcal, decimals, locale)
  return `${kjStr}kJ/${kcalStr}kcal`
}

function resolveRowValue(row, data, decimals, locale) {
  if (row.key === 'energy') return formatEnergy(data, decimals, locale)
  return formatGrams(data[row.valueToken], decimals, locale)
}

/**
 * Draw the nutrition table at (x, y) in canvas pixels.
 */
export function drawNutrition(ctx, el, x, y, zoom, previewMode, previewData, locale, t) {
  const fontSize  = n(el.fontSize, DEFAULT_FONT_SIZE_MM)
  const fontPx    = mmToPx(fontSize) * zoom
  const linePx    = fontPx * LINE_HEIGHT_FACTOR
  const widthMm   = n(el.elementWidth, DEFAULT_WIDTH_MM)
  const widthPx   = mmToPx(widthMm) * zoom
  const weight    = el.fontBold   ? 'bold'   : 'normal'
  const style     = el.fontItalic ? 'italic' : 'normal'
  const family    = el.font_key   || 'Arial, sans-serif'
  const indentPx  = mmToPx(fontSize * 0.7) * zoom
  const subPrefix = el.subRowIndent != null ? el.subRowIndent : '-'

  // Decimals: clamp 0-3, default 1. Token values (designer mode) are
  // literal placeholders like {fat} so decimals don't apply there.
  const decimals = el.decimals != null
    ? Math.min(3, Math.max(0, Number(el.decimals) || 0))
    : DEFAULT_DECIMALS

  ctx.font         = `${style} ${weight} ${fontPx}px ${family}`
  ctx.fillStyle    = '#000000'
  ctx.textBaseline = 'top'

  const hasNutritionData = previewMode && Object.keys(previewData).some(k => k.startsWith('nutrition_'))
  let data
  let useTokens = false
  if (hasNutritionData)   data = previewData
  else if (previewMode)   data = PLACEHOLDER_VALUES
  else                  { data = TOKEN_VALUES; useTokens = true }

  let lineY = y

  if (el.showHeader !== 0 && el.showHeader !== false) {
    ctx.textAlign = 'left'
    ctx.fillText(tr(t, 'nutrition.title') + ':', x, lineY)
    lineY += linePx
  }

  if (el.showReference !== 0 && el.showReference !== false) {
    const ref     = (hasNutritionData && previewData.nutrition_reference)
                    || 'per_100g'
    const portion = hasNutritionData ? previewData.nutrition_portion_size : null
    let refText   = tr(t, referenceKey(ref))
    if (ref === 'per_portion' && portion) refText += ` (${portion})`
    ctx.textAlign = 'left'
    ctx.fillStyle = '#666666'
    ctx.fillText(refText, x, lineY)
    ctx.fillStyle = '#000000'
    lineY += linePx
  }

  const rows = visibleRows(el)
  for (const row of rows) {
    const labelText = (row.isSub ? subPrefix + ' ' : '') + tr(t, `nutrition.${row.i18nKey}`)
    // Token mode (designer) shows literal placeholders without decimal
    // formatting; preview/print modes go through the formatter.
    const valueText = useTokens
      ? (data[row.valueToken] || '')
      : resolveRowValue(row, data, decimals, locale)

    const labelX = row.isSub ? x + indentPx : x
    ctx.textAlign = 'left'
    ctx.fillText(labelText, labelX, lineY)

    if (valueText) {
      ctx.textAlign = 'right'
      ctx.fillText(valueText, x + widthPx, lineY)
    }

    lineY += linePx
  }

  if (!previewMode) {
    const heightPx = lineY - y
    ctx.strokeStyle = 'rgba(68,138,255,0.3)'
    ctx.lineWidth   = 0.5
    ctx.strokeRect(x - 1, y - 1, widthPx + 2, heightPx + 2)
  }
}